import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, FormGroup, Label, Input, Button,
  Alert, Spinner, Nav, NavItem, NavLink, TabContent, TabPane
} from 'reactstrap';
import { User, Mail, Phone, Building, Key, CreditCard, Shield, Save, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import API from '../utils/api';

const Profile = () => {
  const { user, fetchUserProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Profile form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    phone: '',
    company: ''
  });

  // Password change form state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        username: user.username || '',
        phone: user.phone || '',
        company: user.company || ''
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    try {
      await API.put('/auth/profile', formData);
      setMessage('Profile updated successfully');
      // Reload user profile
      await fetchUserProfile();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setMessage('');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');

      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match');
      setLoading(false);
      setTimeout(() => setError(''), 5000);
      return;
    }

    if (passwordData.new_password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      setTimeout(() => setError(''), 5000);
      return;
    }

    try {
      await API.put('/auth/password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      setMessage('Password updated successfully');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setMessage('');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update password');

      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Container fluid className="py-4">
        <div className="text-center">
          <Spinner color="primary" />
          <p className="mt-2 text-muted">Loading profile...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2 className="page-heading">
            <User className="page-heading-icon" />
            User Profile
          </h2>
          <small className="text-muted">Manage your account settings and preferences</small>
        </Col>
      </Row>

      {message && (
        <Alert color="success" className="mb-4">
          {message}
        </Alert>
      )}

      {error && (
        <Alert color="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <Row>
        <Col lg={3}>
          <Card className="bg-dark border-secondary">
            <div className="card-body">
              <Nav vertical pills>
                <NavItem>
                  <NavLink
                    className={activeTab === 'general' ? 'active' : ''}
                    onClick={() => setActiveTab('general')}
                    href="#"
                  >
                    <User size={16} className="me-2" />
                    General Information
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={activeTab === 'security' ? 'active' : ''}
                    onClick={() => setActiveTab('security')}
                    href="#"
                  >
                    <Shield size={16} className="me-2" />
                    Security
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={activeTab === 'billing' ? 'active' : ''}
                    onClick={() => setActiveTab('billing')}
                    href="#"
                  >
                    <CreditCard size={16} className="me-2" />
                    Billing & Credits
                  </NavLink>
                </NavItem>
              </Nav>
            </div>
          </Card>
        </Col>

        <Col lg={9}>
          <TabContent activeTab={activeTab}>
            {/* General Information Tab */}
            <TabPane tabId="general">
              <Card className="bg-dark border-secondary">
                <div className="card-header">
                  <h5 className="mb-0">
                    <User size={20} className="me-2" />
                    General Information
                  </h5>
                </div>
                <div className="card-body">
                  <Form onSubmit={handleSubmitProfile}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="text-white">First Name</Label>
                          <Input
                            type="text"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleInputChange}
                            className="bg-dark text-white"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="text-white">Last Name</Label>
                          <Input
                            type="text"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleInputChange}
                            className="bg-dark text-white"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="text-white">
                            <Mail size={16} className="me-2" />
                            Email Address
                          </Label>
                          <Input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="bg-dark text-white"
                            required
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="text-white">Username</Label>
                          <Input
                            type="text"
                            name="username"
                            value={formData.username}
                            onChange={handleInputChange}
                            className="bg-dark text-white"
                            required
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="text-white">
                            <Phone size={16} className="me-2" />
                            Phone Number
                          </Label>
                          <Input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="bg-dark text-white"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="text-white">
                            <Building size={16} className="me-2" />
                            Company
                          </Label>
                          <Input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            className="bg-dark text-white"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <div className="mt-4">
                      <Button
                        type="submit"
                        color="primary"
                        disabled={loading}
                      >
                        {loading ? (
                          <Spinner size="sm" className="me-2" />
                        ) : (
                          <Save size={16} className="me-2" />
                        )}
                        Update Profile
                      </Button>
                    </div>
                  </Form>
                </div>
              </Card>
            </TabPane>

            {/* Security Tab */}
            <TabPane tabId="security">
              <Card className="bg-dark border-secondary">
                <div className="card-header">
                  <h5 className="mb-0">
                    <Shield size={20} className="me-2" />
                    Security Settings
                  </h5>
                </div>
                <div className="card-body">
                  <Form onSubmit={handleSubmitPassword}>
                    <FormGroup>
                      <Label className="text-white">Current Password</Label>
                      <div className="position-relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          name="current_password"
                          value={passwordData.current_password}
                          onChange={handlePasswordChange}
                          className="bg-dark text-white"
                          required
                        />
                        <Button
                          type="button"
                          color="link"
                          className="position-absolute end-0 top-0 text-muted border-0"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </Button>
                      </div>
                    </FormGroup>

                    <FormGroup>
                      <Label className="text-white">New Password</Label>
                      <Input
                        type="password"
                        name="new_password"
                        value={passwordData.new_password}
                        onChange={handlePasswordChange}
                        className="bg-dark text-white"
                        required
                        minLength={8}
                      />
                      <small className="text-muted">Password must be at least 8 characters long</small>
                    </FormGroup>

                    <FormGroup>
                      <Label className="text-white">Confirm New Password</Label>
                      <Input
                        type="password"
                        name="confirm_password"
                        value={passwordData.confirm_password}
                        onChange={handlePasswordChange}
                        className="bg-dark text-white"
                        required
                      />
                    </FormGroup>

                    <div className="mt-4">
                      <Button
                        type="submit"
                        color="warning"
                        disabled={loading}
                      >
                        {loading ? (
                          <Spinner size="sm" className="me-2" />
                        ) : (
                          <Key size={16} className="me-2" />
                        )}
                        Change Password
                      </Button>
                    </div>
                  </Form>

                  <hr className="my-4" />

                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="text-white">Account Status</h6>
                      <p className="text-muted">
                        Role: <span className="text-warning">{user.role}</span>
                      </p>
                      <p className="text-muted">
                        Status: <span className={user.is_active ? 'text-success' : 'text-danger'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </p>
                      <p className="text-muted">
                        Email Verified: <span className={user.email_verified ? 'text-success' : 'text-warning'}>
                          {user.email_verified ? 'Yes' : 'Pending'}
                        </span>
                      </p>
                    </div>
                    <div className="col-md-6">
                      <h6 className="text-white">Account Created</h6>
                      <p className="text-muted">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </TabPane>

            {/* Billing Tab */}
            <TabPane tabId="billing">
              <Card className="bg-dark border-secondary">
                <div className="card-header">
                  <h5 className="mb-0">
                    <CreditCard size={20} className="me-2" />
                    Billing & Credits
                  </h5>
                </div>
                <div className="card-body">
                  <Row>
                    <Col md={6}>
                      <div className="text-center p-4 bg-primary rounded">
                        <h3 className="text-white mb-1">
                          {user.currency} {user.credit_balance || '0.00'}
                        </h3>
                        <p className="text-light mb-0">Available Credits</p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="text-center p-4 bg-dark border border-secondary rounded">
                        <h6 className="text-white mb-3">Payment Methods</h6>
                        <p className="text-muted">No payment methods added</p>
                        <Button color="outline-primary" size="sm">
                          Add Payment Method
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <hr className="my-4" />

                  <h6 className="text-white mb-3">Recent Transactions</h6>
                  <div className="text-center py-4">
                    <p className="text-muted">No recent transactions</p>
                  </div>
                </div>
              </Card>
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </Container>
  );
};

export default Profile;